import { useCallback, useRef } from "react";
import { createExpiringStorageEntry, isNonEmptyString } from "../../lib/expiringStorageEntry";
import { INSTALL_NONCE_COOKIE } from "../../lib/shopifyInstallRedirect";
import { useResolvedAdvertiserGate } from "./useResolvedAdvertiserGate";

// DEV-2332: Shopify's OAuth-first install (begin-install) means the OAuth
// callback can now redirect BEFORE anyone is signed in to Audiohook. The
// callback stages the credential (an install-origin pending_connections row,
// 088) and redirects with ?status=pending&confirm=<token>&origin=install
// &connect_shop=<shop>. AuthGate's ?integration= effect persists that
// { shop, confirmToken } here (persistShopifyPendingConfirm) so it survives
// the redirect to /login and a possible magic-link round trip through the
// merchant's inbox, distinct from the confirmed bound (in-app, DEV-2211)
// flow which auto-confirms immediately with no persistence at all.
//
// This file previously (DEV-2299) persisted the RAW captured install query
// and resumed start-install once signed in, BEFORE reaching Shopify's OAuth
// screen — that path is removed: DEV-2332 replaces it with begin-install,
// called directly from AuthGate's capture effect with no session required,
// so OAuth starts immediately and nothing needs to survive a login round
// trip until AFTER the callback. What now needs to survive login is the
// CONFIRM step: the merchant must be signed in, with an advertiser resolved,
// before confirm_pending_connection can bind the staged connection to real
// advertiser/user ids.
export const SHOPIFY_PENDING_CONFIRM_STORAGE_KEY = "cpa_ui_shopify_pending_confirm";

// Mirrors 088's install-origin pending_connections TTL (create_pending_connection's
// p_ttl for an install-origin row) — see shopify-oauth/index.ts's
// handleCallbackInner.
const PENDING_CONFIRM_TTL_MS = 60 * 60 * 1000; // 1 hour

// DEV-2332 code-review round: a SEPARATE localStorage entry holding the
// random nonce AuthGate generates and sends with begin-install, keyed by the
// shop it was generated for. This is what binds the eventual confirm to the
// BROWSER that started the install, not merely to whoever holds the confirm
// token (a URL param leaks via PostHog/history/server logs far more easily
// than a localStorage entry). The install-origin prompt's shop name is read
// from HERE, never from the `connect_shop` URL query param the callback
// redirect carries — that param is not trustworthy on its own (it could be
// spoofed independently of the token). If this entry is missing or expired
// by the time the pending confirm would otherwise surface, no prompt is
// shown at all: the server independently enforces the same rule (a
// null-owner pending_connections row requires a matching install_nonce_hash
// to confirm, see 088's confirm_pending_connection), but the SPA fails
// closed too rather than showing a prompt it already knows cannot succeed.
export const SHOPIFY_INSTALL_NONCE_STORAGE_KEY = "cpa_ui_shopify_install_nonce";
const INSTALL_NONCE_TTL_MS = 60 * 60 * 1000; // 1 hour

const pendingConfirmEntry = createExpiringStorageEntry(
  SHOPIFY_PENDING_CONFIRM_STORAGE_KEY,
  PENDING_CONFIRM_TTL_MS,
  ({ shop, confirmToken }) => (isNonEmptyString(shop) && isNonEmptyString(confirmToken) ? { shop, confirmToken } : null),
);

const installNonceEntry = createExpiringStorageEntry(
  SHOPIFY_INSTALL_NONCE_STORAGE_KEY,
  INSTALL_NONCE_TTL_MS,
  ({ nonce, shop }) => (isNonEmptyString(nonce) && isNonEmptyString(shop) ? { nonce, shop } : null),
);

/**
 * Persists a staged install-origin confirm ({ shop, confirmToken }) so it
 * survives the redirect to /login. Called synchronously from AuthGate's
 * ?integration= effect when status=pending&origin=install arrives —
 * regardless of session state, the same reasoning DEV-2299's capture effect
 * used for the raw install query.
 */
export function persistShopifyPendingConfirm({ shop, confirmToken }) {
  // localStorage may be unavailable (private mode): the merchant can still
  // connect from the Integrations panel's Connect card.
  pendingConfirmEntry.persist({ shop, confirmToken });
}

/** Clears the persisted entry. Called once the prompt is acted on (Connect or Not now), or found expired. */
export function clearShopifyPendingConfirm() {
  pendingConfirmEntry.clear();
}

/**
 * Persists the install nonce (DEV-2332 code-review round): called from
 * AuthGate's install-capture effect right before it calls begin-install,
 * BEFORE the network request — so the nonce survives even if the tab
 * navigates away to Shopify's authorize screen immediately after.
 *
 * Second review round (item 6): returns whether the write actually landed —
 * `localStorage.setItem` can throw (private mode, a full quota), but it can
 * also silently no-op in some browsers/embedded webviews without throwing
 * at all, so this reads the value straight back and compares it rather than
 * trusting the absence of a thrown error. AuthGate's capture effect must
 * NOT call begin-install when this returns false: without this entry, the
 * eventual confirm step can never succeed (088's confirm_pending_connection
 * requires the matching install_nonce_hash for a null-owner row), so
 * starting OAuth anyway would just strand the merchant on Shopify's side
 * with no way back.
 */
export function persistShopifyInstallNonce({ nonce, shop }) {
  return installNonceEntry.persist({ nonce, shop });
}

/**
 * Moves the install nonce cookie (set by middleware.js when it answers
 * Shopify's install request server-side) into the localStorage entry the
 * confirm step reads, keeping the cookie's own savedAt, then deletes the
 * cookie. A malformed cookie is deleted and nothing is stored. No cookie is a
 * no-op. Must run on app load before anything reads the nonce.
 */
export function adoptInstallNonceCookie() {
  try {
    const prefix = `${INSTALL_NONCE_COOKIE}=`;
    const pair = document.cookie.split("; ").find((c) => c.startsWith(prefix));
    if (pair === undefined) return;
    const deleteCookie = () => {
      document.cookie = `${INSTALL_NONCE_COOKIE}=; Max-Age=0; Path=/`;
    };
    let entry = null;
    try {
      const { nonce, shop, savedAt } = JSON.parse(decodeURIComponent(pair.slice(prefix.length))) || {};
      if (typeof nonce === "string" && nonce && typeof shop === "string" && shop
        && typeof savedAt === "number" && Number.isFinite(savedAt)) {
        entry = { nonce, shop, savedAt };
      }
    } catch {
      // Malformed cookie: dropped below.
    }
    if (!entry) {
      deleteCookie();
      return;
    }
    // Store first, delete after: if storage throws, the cookie survives for a
    // later load. The install params are gone from the URL after the
    // server-side redirect, so the in-page capture cannot recreate the nonce.
    localStorage.setItem(SHOPIFY_INSTALL_NONCE_STORAGE_KEY, JSON.stringify(entry));
    deleteCookie();
  } catch {
    // Storage unavailable: the cookie is kept and adopted on a later load.
  }
}

/** Reads back the persisted install nonce, or null if missing/malformed/expired. */
export function readShopifyInstallNonce() {
  return installNonceEntry.read();
}

/** Clears the persisted install nonce. Called once it is consumed (a confirm attempt) or explicitly declined (Not now), and on signOut. */
export function clearShopifyInstallNonce() {
  installNonceEntry.clear();
}

/**
 * Surfaces a persisted install-origin pending confirm (persistShopifyPendingConfirm
 * above) once the user is signed in with an advertiser resolved, as an
 * explicit prompt — never an automatic confirm, since an attacker could send
 * their own store's confirm link to an Audiohook user (DEV-2332's design
 * note). Reuses `openIntegrationsResult` (AuthGate's setIntegrationResult),
 * the same one-shot "open the Integrations panel" mechanism the OAuth
 * callback path already uses, with a distinct status
 * ("pending_confirm_prompt") that SalesIntegrationsSection renders as a
 * Connect / Not now prompt rather than an automatic confirm.
 *
 * Gating mirrors DEV-2299's original hook: waits for the ?advertiser_id=
 * masquerade deep link to be applied first, waits for advertisersLoaded and
 * a resolved effectiveAdvertiserId. A support user with NO active masquerade
 * is the one deliberate difference from DEV-2299: the entry is KEPT, not
 * discarded, since they may masquerade as the target advertiser next.
 */
export function useShopifyPendingConfirm({ openIntegrationsResult }) {
  // The confirmToken most recently surfaced, so a re-render (or a later,
  // different pending entry) doesn't re-fire the same prompt endlessly.
  const firedForTokenRef = useRef(null);

  const surfacePending = useCallback(() => {
    // Read without the TTL so an expired entry is cleared below, while a
    // malformed one is left as it is.
    const pending = pendingConfirmEntry.read({ includeExpired: true });
    if (!pending) return;

    if (Date.now() - pending.savedAt > PENDING_CONFIRM_TTL_MS) {
      clearShopifyPendingConfirm();
      return;
    }

    // DEV-2332 code-review round: never surface a prompt without a matching
    // install nonce entry still in localStorage — that entry is the proof
    // this IS the browser that started the install, and its `shop` (never
    // the pending entry's own shop, which could in principle trace back to
    // an untrusted URL param) is what the prompt shows. Missing/expired
    // means show nothing at all, rather than a prompt the server is
    // guaranteed to reject anyway (088's confirm_pending_connection requires
    // the matching install_nonce_hash).
    const nonceEntry = readShopifyInstallNonce();
    if (!nonceEntry) {
      clearShopifyPendingConfirm();
      return;
    }

    if (firedForTokenRef.current === pending.confirmToken) return;
    firedForTokenRef.current = pending.confirmToken;

    openIntegrationsResult({
      source: "shopify",
      status: "pending_confirm_prompt",
      shop: nonceEntry.shop,
      confirmToken: pending.confirmToken,
      installNonce: nonceEntry.nonce,
      reason: null,
    });
  }, [openIntegrationsResult]);

  // A support user with NO active masquerade keeps the entry (the gate never
  // calls surfacePending for them): unlike DEV-2299's original install-start
  // flow, they may masquerade as the target advertiser next, and the confirm
  // link is only good for its 1 hour TTL either way.
  useResolvedAdvertiserGate(surfacePending);
}
